import { BlameLogRefType, BlameLogType, } from '@debbie/common/dist/blamelog/index.js';
import keys from '@debbie/common/dist/cache/keys.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as creditorsAPI from '@debbie/cortex/dist/api/creditors.js';
import * as debtRegisterAPI from '@debbie/cortex/dist/api/debt-register.js';
import { blameLogs } from '@debbie/cortex/dist/api/index.js';
import { creditorsEntity, useCreditor, } from '@debbie/cortex/dist/creditors/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import * as notifications from '@debbie/cortex/dist/notifications.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { Size } from '../design-system.js';
import THEME from '../theme.js';
import { errorNotification } from '../utils/errors.js';
import { Button, Checkbox, Header, Input, Modal, SmallHeader, Well, } from './index.js';
import OverrideSetting from './OverrideSetting.js';
import PlaceholderLoader from './PlaceholderLoader.js';
const CreditorExperianConfig = (props) => {
    const { id } = props;
    const lang = useSelector((s) => s.lang);
    const creditorLoadable = useCreditor(id);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const tenant = useTenant();
    const meRole = useRole();
    const [activeTab, setActiveTab] = useState('general');
    const [aksEnabled, setAksEnabled] = useState(false);
    const [experianClientId, setExperianClientId] = useState(null);
    const [autoDebtRegisterModelOpen, setAutoDebtRegisterModelOpen] = useState(false);
    const [state, setState] = useState({
        autoDebtRegister: creditor?.settings.autoDebtRegister !== undefined
            ? creditor?.settings.autoDebtRegister
            : tenant?.settings?.autoDebtRegister
                ? tenant.settings.autoDebtRegister
                : false,
        overrideAutoDebtRegister: false,
        autoRegisterInOpenRegistry: false,
        autoIgnoreUnresolvedOppositions: false,
    });
    useEffect(() => {
        if (creditor && tenant) {
            setAksEnabled(creditor.aksEnabled);
            setExperianClientId(creditor.experianClientId || null);
            setState({
                ...state,
                overrideAutoDebtRegister: creditor.settings.overrideAutoDebtRegister || false,
                autoDebtRegister: creditor.settings.autoDebtRegister === undefined
                    ? !!tenant.settings.autoDebtRegister
                    : creditor.settings.autoDebtRegister,
                autoRegisterInOpenRegistry: creditor.settings.autoRegisterInOpenRegistry === undefined
                    ? !!tenant.settings.autoRegisterInOpenRegistry
                    : creditor.settings.autoRegisterInOpenRegistry || false,
                autoIgnoreUnresolvedOppositions: creditor.settings?.autoIgnoreUnresolvedOppositions === undefined
                    ? !!tenant.settings.autoIgnoreUnresolvedOppositions
                    : creditor.settings.autoIgnoreUnresolvedOppositions || false,
            });
        }
    }, [creditor, tenant]);
    const updateAutoDebtRegister = async (_creditor, autoDebtRegister) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                autoDebtRegister,
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateAutoRegisterInOpenRegistry = async (_creditor, autoRegisterInOpenRegistry) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                autoRegisterInOpenRegistry,
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateAutoIgnoreUnresolvedOppositions = async (_creditor, autoIgnoreUnresolvedOppositions) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                autoIgnoreUnresolvedOppositions,
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateOverrideAutoDebtRegister = async (_creditor, overrideAutoDebtRegister, autoDebtRegister, autoRegisterInOpenRegistry, autoIgnoreUnresolvedOppositions) => {
        if (overrideAutoDebtRegister) {
            await creditorsAPI.update(id, {
                settings: {
                    ..._creditor.settings,
                    overrideAutoDebtRegister,
                    autoDebtRegister,
                    autoRegisterInOpenRegistry,
                    autoIgnoreUnresolvedOppositions,
                },
            });
        }
        else {
            await creditorsAPI.update(id, {
                settings: {
                    ..._creditor.settings,
                    overrideAutoDebtRegister,
                    autoDebtRegister: undefined,
                    autoRegisterInOpenRegistry: undefined,
                    autoIgnoreUnresolvedOppositions: undefined,
                },
            });
        }
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const saveOverrideAutoDebtRegister = useMutation(async (_creditor, checked) => {
        await updateOverrideAutoDebtRegister(_creditor, checked, state.autoDebtRegister, state.autoRegisterInOpenRegistry, state.autoIgnoreUnresolvedOppositions);
        if (tenant != null) {
            await blameLogs.createBlameLog({
                details: { toggled: checked },
                refId: _creditor.creditorId,
                refType: BlameLogRefType.Creditor,
                type: BlameLogType.RKIOveride,
            }, tenant.id);
        }
    }, {
        onError: (error) => errorNotification(lang, error, 'update', lang.errorEntities.settings),
    });
    const saveAutoDebtRegister = useMutation(async (_creditor, checked) => {
        await updateAutoDebtRegister(_creditor, checked);
        if (tenant != null) {
            await blameLogs.createBlameLog({
                details: { toggled: checked },
                refId: _creditor.creditorId,
                refType: BlameLogRefType.Creditor,
                type: BlameLogType.AutoDebtRegister,
            }, tenant.id);
        }
    }, {
        onError: (error) => errorNotification(lang, error, 'update', lang.errorEntities.settings),
    });
    const saveAutoRegisterInOpenRegistry = useMutation(async (_creditor, checked) => {
        await updateAutoRegisterInOpenRegistry(_creditor, checked);
        if (tenant != null) {
            await blameLogs.createBlameLog({
                details: { toggled: checked },
                refId: _creditor.creditorId,
                refType: BlameLogRefType.Creditor,
                type: BlameLogType.AutoRegisterInOpenRegistry,
            }, tenant.id);
        }
    }, {
        onError: (error) => errorNotification(lang, error, 'update', lang.errorEntities.settings),
    });
    const saveAutoIgnoreUnresolvedOppositions = useMutation(async (_creditor, checked) => {
        await updateAutoIgnoreUnresolvedOppositions(_creditor, checked);
        if (tenant != null) {
            await blameLogs.createBlameLog({
                details: { toggled: checked },
                refId: _creditor.creditorId,
                refType: BlameLogRefType.Creditor,
                type: BlameLogType.AutoIgnoreUnresolvedOppositions,
            }, tenant.id);
        }
    }, {
        onError: (error) => errorNotification(lang, error, 'update', lang.errorEntities.settings),
    });
    const saveExperianClientId = useMutation(async (_creditor, clientId) => {
        await creditorsAPI.update(_creditor.creditorId, {
            experianClientId: clientId,
        });
        if (tenant != null) {
            await blameLogs.createBlameLog({
                details: { clientId },
                refId: _creditor.creditorId,
                refType: BlameLogRefType.Creditor,
                type: BlameLogType.AKSClientID,
            }, tenant.id);
        }
        q.invalidate(keys.write.modify(creditorsEntity, id));
    }, {
        onSuccess: () => setExperianClientId(null),
        onError: (error) => errorNotification(lang, error, 'update', lang.errorEntities.creditor),
    });
    const requestExperianClientId = useMutation((_creditor) => creditorsAPI.experianAskForClientId(_creditor.creditorId), {
        onSuccess: () => notifications.notification({
            title: lang.creditors.requestClientIdExperianSuccess,
            theme: 'success',
        }),
        onError: (error) => errorNotification(lang, error, 'create', lang.creditors.experianClientId),
    });
    const saveAksEnabled = useMutation(async (_creditor, checked) => {
        await creditorsAPI.update(_creditor.creditorId, {
            aksEnabled: checked,
        });
        if (tenant != null) {
            await blameLogs.createBlameLog({
                details: { toggled: checked },
                refId: _creditor.creditorId,
                refType: BlameLogRefType.Creditor,
                type: BlameLogType.AKSActivated,
            }, tenant.id);
        }
    }, {
        onSuccess: (_data, _creditor, checked) => setAksEnabled(checked),
        onError: (error) => errorNotification(lang, error, 'update', lang.errorEntities.settings),
    });
    const dbrSetup = useMutation((creditorId) => debtRegisterAPI.dbrSetupCreditor(creditorId), {
        onSuccess: (result) => {
            if (result.success > 0) {
                q.invalidate(keys.write.modify(creditorsEntity, id));
            }
        },
        onError: (error) => errorNotification(lang, error, 'create', lang.errorEntities.creditor),
    });
    const dbrSetupResult = dbrSetup.data;
    return (React.createElement("div", null, creditor ? (React.createElement(React.Fragment, null,
        React.createElement(TabList, null,
            React.createElement(TabButton, { $active: activeTab === 'general', onClick: () => setActiveTab('general') }, lang.debtRegistration.tabGeneral),
            React.createElement(TabButton, { $active: activeTab === 'rki', onClick: () => setActiveTab('rki') }, lang.debtRegistration.tabRki),
            React.createElement(TabButton, { $active: activeTab === 'debitorregistret', onClick: () => setActiveTab('debitorregistret') }, lang.debtRegistration.tabDebitorRegistret)),
        activeTab === 'general' && (React.createElement("div", null, tenant && meRole && isAdmin(meRole) && (React.createElement(Well, null,
            React.createElement(Header, { size: '3', style: { marginTop: 0 } }, lang.creditors.autoRKI),
            React.createElement(OverrideSetting, { infoIcon: true, iconTip: lang.tenants.autoDebtRegisterInfoTip, id: 'auto-debt-register', checked: state.overrideAutoDebtRegister, placeholder: lang.creditors.automaticDebtRegister, style: { width: '100%' }, loading: saveOverrideAutoDebtRegister.isLoading, disabled: saveOverrideAutoDebtRegister.isLoading, onChange: (e) => {
                    saveOverrideAutoDebtRegister.mutate(creditor, e.target.checked);
                }, iconOnClick: () => {
                    setAutoDebtRegisterModelOpen(!autoDebtRegisterModelOpen);
                } },
                React.createElement(Checkbox, { disabled: !state.overrideAutoDebtRegister ||
                        saveAutoDebtRegister.isLoading, loading: saveAutoDebtRegister.isLoading, style: { marginLeft: '2em' }, size: Size.Small, setting: true, placeholder: lang.creditors.useAutomaticDebtRegister, checked: state.autoDebtRegister, onChange: (e) => {
                        saveAutoDebtRegister.mutate(creditor, e.target.checked);
                    } }),
                React.createElement(Checkbox, { disabled: !state.overrideAutoDebtRegister ||
                        saveAutoRegisterInOpenRegistry.isLoading, loading: saveAutoRegisterInOpenRegistry.isLoading, style: { marginLeft: '2em' }, size: Size.Small, setting: true, placeholder: lang.creditors.useAutoRegisterInOpenRegistry, checked: state.autoRegisterInOpenRegistry, onChange: (e) => {
                        saveAutoRegisterInOpenRegistry.mutate(creditor, e.target.checked);
                    } }),
                React.createElement(Checkbox, { disabled: !state.overrideAutoDebtRegister ||
                        saveAutoIgnoreUnresolvedOppositions.isLoading, loading: saveAutoIgnoreUnresolvedOppositions.isLoading, style: { marginLeft: '2em' }, size: Size.Small, setting: true, placeholder: lang.creditors.useAutoIgnoreUnresolvedOppositions, checked: state.autoIgnoreUnresolvedOppositions, onChange: (e) => {
                        saveAutoIgnoreUnresolvedOppositions.mutate(creditor, e.target.checked);
                    } })))))),
        activeTab === 'rki' && (React.createElement("div", null,
            React.createElement(SmallHeader, null, lang.creditors.experianClientId),
            React.createElement(Input, { type: "number", button: experianClientId !== creditor.experianClientId
                    ? {
                        value: lang.creditors.updateExperianClientId,
                        loading: saveExperianClientId.isLoading,
                        onClick: () => {
                            saveExperianClientId.mutate(creditor, experianClientId);
                        },
                    }
                    : !creditor.experianClientId &&
                        !requestExperianClientId.isSuccess
                        ? {
                            value: lang.creditors.requestClientIdExperian,
                            onClick: () => {
                                requestExperianClientId.mutate(creditor);
                            },
                            loading: requestExperianClientId.isLoading,
                        }
                        : undefined, placeholder: lang.creditors.experianClientId, value: experianClientId || '', onChange: (e) => setExperianClientId(e.target.value) }),
            React.createElement(Checkbox, { placeholder: lang.creditors.aksEnabled, checked: aksEnabled, loading: saveAksEnabled.isLoading, disabled: saveAksEnabled.isLoading, onChange: (e) => {
                    saveAksEnabled.mutate(creditor, e.target.checked);
                }, setting: true, size: Size.Small }))),
        activeTab === 'debitorregistret' && (React.createElement("div", null,
            React.createElement(SmallHeader, null, lang.creditors.debitorRegistretCreditorNumber),
            React.createElement("p", null, creditor.debitorRegistretCreditorNumber ?? '—'),
            creditor.debitorRegistretCreditorNumber == null && (React.createElement(React.Fragment, null,
                React.createElement("p", null, lang.creditors.debitorRegistretCreateCreditorDescription),
                React.createElement(Button, { loading: dbrSetup.isLoading, onClick: () => {
                        // Clear the previous attempt's results so a retry never
                        // shows stale success/failure counts while running.
                        dbrSetup.reset();
                        dbrSetup.mutate(creditor.creditorId);
                    } }, lang.creditors.debitorRegistretCreateCreditor))),
            dbrSetupResult && (React.createElement("div", { style: { marginTop: '12px' } },
                dbrSetupResult.success > 0 && (React.createElement("p", null, lang.creditors.debitorRegistretCreateCreditorSuccess)),
                dbrSetupResult.skipped > 0 && (React.createElement("p", null, lang.creditors.debitorRegistretCreateCreditorSkipped)),
                dbrSetupResult.failed.map((f) => (React.createElement("p", { key: f.creditorId, style: { color: THEME.DANGEROUS } }, f.error))))))),
        autoDebtRegisterModelOpen && (React.createElement(Modal, { header: lang.tenants.automaticDebtRegister, onToggle: () => setAutoDebtRegisterModelOpen(!autoDebtRegisterModelOpen), maxWidth: "650px" },
            React.createElement(Well, null,
                React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.headline),
                React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.info),
                React.createElement("ul", null,
                    React.createElement("li", null, lang.debtRegistration.infobox.min3messages),
                    React.createElement("li", null, lang.debtRegistration.infobox.debtorWarning),
                    React.createElement("li", null, lang.debtRegistration.infobox.closedRegister),
                    React.createElement("ul", null,
                        React.createElement("li", null, lang.debtRegistration.infobox.totalPrincipal),
                        React.createElement("li", null, lang.debtRegistration.infobox.incoming)),
                    React.createElement("li", null, lang.debtRegistration.infobox.openRegister),
                    React.createElement("ul", null,
                        React.createElement("li", null, lang.debtRegistration.infobox.totalDebt),
                        React.createElement("li", null, lang.debtRegistration.infobox.hasFoundation),
                        React.createElement("li", null, lang.debtRegistration.infobox.foundationType))),
                React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.exclude),
                React.createElement("ul", null,
                    React.createElement("li", null, lang.debtRegistration.infobox.min18years),
                    React.createElement("li", null, lang.debtRegistration.infobox.specialCreditors)),
                React.createElement("p", null, lang.tenants.autoDebtRegisterRKI.endingInfo)))))) : (React.createElement(PlaceholderLoader, null))));
};
export default CreditorExperianConfig;
const TabList = styled.div `
  padding: 5px;
  margin-bottom: 20px;
  background-color: ${THEME.FILL};
  border-radius: 3px;
`;
const TabButton = styled.button `
  display: inline-block;
  cursor: pointer;
  font-weight: 900;
  padding: 7px 12px;
  margin-right: 5px;
  border-radius: 3px;
  border: none;
  background-color: ${(p) => p.$active ? THEME.COLOR_SECONDARY : 'transparent'};
  color: ${(p) => (p.$active ? THEME.COLOR_ACCENT : THEME.TEXT_MUTED)};
  font-size: 0.85em;

  &:hover {
    background-color: ${(p) => (p.$active ? THEME.COLOR_SECONDARY : '#b8bbbe')};
    color: inherit;
  }
`;
//# sourceMappingURL=CreditorExperianConfig.js.map